import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, MagnifyingGlassIcon, UserPlusIcon, CheckIcon, ChartPieIcon } from '@heroicons/react/24/outline'

const getInitials = (nome) => {
    if (!nome) return '?'
    return nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

const ModalVincularInvestidorObra = ({ open, onClose, onSave, onSearchInvestidor, investidoresDisponiveis = [], loadingSearch }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedInvestidor, setSelectedInvestidor] = useState(null)
    const [porcentagem, setPorcentagem] = useState('')
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!open) {
            setSearchTerm('')
            setSelectedInvestidor(null)
            setPorcentagem('')
            setError(null)
        }
    }, [open])

    const handleSearch = () => {
        if (!searchTerm.trim()) return
        onSearchInvestidor?.(searchTerm)
    }

    const handleSubmit = () => {
        if (!selectedInvestidor) { setError('Selecione um investidor'); return }
        const pct = parseFloat(porcentagem)
        if (!porcentagem || isNaN(pct) || pct <= 0 || pct > 100) {
            setError('Informe uma porcentagem entre 0 e 100')
            return
        }
        setError(null)
        onSave({
            investidor_id: selectedInvestidor.id,
            porcentagem: String(pct),
        })
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">
                                <div className="relative bg-gray-900 px-6 pt-6 pb-14 overflow-hidden">
                                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-brand-yellow/10" />
                                    <div className="relative flex items-start justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-brand-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                                                <UserPlusIcon className="w-7 h-7 text-gray-900" />
                                            </div>
                                            <div>
                                                <Dialog.Title as="h3" className="text-xl font-bold text-white">Vincular à Obra</Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">Busque o investidor e defina a participação</p>
                                            </div>
                                        </div>
                                        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="relative -mt-6 mx-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 space-y-4">
                                        <div>
                                            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                                                <MagnifyingGlassIcon className="w-3.5 h-3.5" />Buscar Investidor
                                            </label>
                                            <div className="flex gap-2 mt-1.5">
                                                <input
                                                    type="text"
                                                    value={searchTerm}
                                                    onChange={e => setSearchTerm(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                                    placeholder="Nome ou e-mail..."
                                                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-brand-yellow transition-all"
                                                />
                                                <button
                                                    onClick={handleSearch}
                                                    disabled={loadingSearch}
                                                    className="px-4 py-2.5 rounded-xl bg-gray-900 text-brand-yellow text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50"
                                                >
                                                    {loadingSearch
                                                        ? <span className="h-4 w-4 rounded-full border-2 border-brand-yellow/30 border-t-brand-yellow animate-spin inline-block" />
                                                        : <MagnifyingGlassIcon className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>

                                        {investidoresDisponiveis.length > 0 && (
                                            <div>
                                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                                    {searchTerm.trim() ? 'Resultados da busca' : 'Investidores mais recentes'}
                                                </p>
                                                <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                                                {investidoresDisponiveis.map((inv) => (
                                                    <button
                                                        key={inv.id}
                                                        type="button"
                                                        onClick={() => { setSelectedInvestidor(inv); setError(null) }}
                                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                                                            selectedInvestidor?.id === inv.id
                                                                ? 'border-brand-yellow bg-brand-yellow/10'
                                                                : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <div className="pointer-events-none w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center text-brand-yellow text-xs font-bold flex-shrink-0">
                                                            {getInitials(inv.nome)}
                                                        </div>
                                                        <div className="pointer-events-none min-w-0 flex-1">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{inv.nome}</p>
                                                            <p className="text-xs text-gray-400 truncate">{inv.email}</p>
                                                        </div>
                                                        {selectedInvestidor?.id === inv.id && (
                                                            <CheckIcon className="pointer-events-none w-4 h-4 text-brand-yellow flex-shrink-0" />
                                                        )}
                                                    </button>
                                                ))}
                                                </div>
                                            </div>
                                        )}

                                        {selectedInvestidor && (
                                            <div>
                                                <label htmlFor="porcentagem" className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                                    <ChartPieIcon className="w-3.5 h-3.5" />Participação (%)
                                                </label>
                                                <input
                                                    id="porcentagem"
                                                    type="number"
                                                    min="0.01"
                                                    max="100"
                                                    step="0.01"
                                                    value={porcentagem}
                                                    onChange={(e) => { setPorcentagem(e.target.value); setError(null) }}
                                                    placeholder="Ex.: 50"
                                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-brand-yellow"
                                                />
                                            </div>
                                        )}

                                        {error && <p className="text-xs text-red-500 flex items-center gap-1"><span>⚠</span>{error}</p>}
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-6">
                                    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all">Cancelar</button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={!selectedInvestidor}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <UserPlusIcon className="w-4 h-4" /> Vincular
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalVincularInvestidorObra
